import { useState } from 'react'
import { FloppyDisk, Trash, Lightning, CaretDown, CaretUp, Plus } from '@phosphor-icons/react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { useSavedTemplates, SavedTemplate } from '../../hooks/useSavedTemplates'
import { BreakdownItem, OfferCurrency } from './FeeBreakdown'

interface SavedTemplatesPanelProps {
  modality: string
  currentBreakdown: BreakdownItem[]
  currency: OfferCurrency
  onApplyTemplate: (breakdown: BreakdownItem[]) => void
}

export function SavedTemplatesPanel({
  modality,
  currentBreakdown,
  currency,
  onApplyTemplate,
}: SavedTemplatesPanelProps) {
  const { templates, saveTemplate, deleteTemplate, applyTemplate } = useSavedTemplates(modality)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showSaveForm, setShowSaveForm] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const [saving, setSaving] = useState(false)

  // Only show items with values
  const hasValidBreakdown = currentBreakdown.some(item => item.value > 0)

  const handleSaveTemplate = () => {
    if (!newTemplateName.trim() || !hasValidBreakdown) return

    setSaving(true)
    try {
      saveTemplate(newTemplateName.trim(), currentBreakdown.filter(item => item.value > 0), modality)
      setNewTemplateName('')
      setShowSaveForm(false)
    } finally {
      setSaving(false)
    }
  }

  const handleApply = (template: SavedTemplate) => {
    const breakdown = applyTemplate(template, currency)
    onApplyTemplate(breakdown)
  }

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    if (window.confirm('Tem certeza que deseja excluir este template?')) {
      deleteTemplate(id)
    }
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    })
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Lightning size={18} color="#f59e0b" weight="fill" />
          <span className="font-medium text-gray-900">Templates Salvos</span>
          {templates.length > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
              {templates.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <CaretUp size={18} color="#6b7280" />
        ) : (
          <CaretDown size={18} color="#6b7280" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-3">
          {/* Save current as template */}
          {!showSaveForm ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full gap-2"
              onClick={() => setShowSaveForm(true)}
              disabled={!hasValidBreakdown}
              title={!hasValidBreakdown ? 'Adicione valores às taxas primeiro' : undefined}
            >
              <Plus size={16} />
              Salvar Atual como Template
            </Button>
          ) : (
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Nome do template..."
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleSaveTemplate()
                  }
                  if (e.key === 'Escape') {
                    setShowSaveForm(false)
                    setNewTemplateName('')
                  }
                }}
                className="flex-1 h-9"
                autoFocus
              />
              <Button
                type="button"
                size="sm"
                onClick={handleSaveTemplate}
                disabled={!newTemplateName.trim() || saving}
                className="gap-1"
              >
                <FloppyDisk size={16} />
                Salvar
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowSaveForm(false)
                  setNewTemplateName('')
                }}
              >
                Cancelar
              </Button>
            </div>
          )}

          {/* Templates list */}
          {templates.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              Nenhum template salvo para esta modalidade.
              <br />
              <span className="text-xs">Preencha os valores e salve para usar depois!</span>
            </p>
          ) : (
            <div className="space-y-2">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between p-3 bg-gray-50 hover:bg-amber-50 rounded-lg border border-gray-200 hover:border-amber-200 cursor-pointer transition-colors group"
                  onClick={() => handleApply(template)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{template.name}</p>
                    <p className="text-xs text-gray-500">
                      {template.breakdown.length} taxas • Salvo em {formatDate(template.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-amber-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Clique para aplicar
                    </span>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, template.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                      title="Excluir template"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
